import React from 'react';
import PropTypes from 'prop-types';
import { formatCalculatedNumber } from '../hooks/useV3OfferBudget';
import { hasBreakevenColumn, isUnachievable, breakevenCountLabel } from '../utils/breakevenDisplay';
import '../styles/breakevenTooltip.css';

// and the advanced editor's deal-terms readout so the explanation cannot drift
// between the two surfaces (O-4774).
const DASH = '—';

const TicketsToBreakevenTooltip = ({ offeredDetail, forecastDetail }) => {
  if (!hasBreakevenColumn(offeredDetail)) return null;

  const hasForecast = hasBreakevenColumn(forecastDetail);
  const columnCount = hasForecast ? 3 : 2;
  const offeredImpossible = isUnachievable(offeredDetail);
  const forecastImpossible = isUnachievable(forecastDetail);

  const money = (n) => formatCalculatedNumber(Number(n || 0));
  const count = (n) => Number(n || 0).toLocaleString();

  const cell = (detail, impossible, key, negated) => {
    if (impossible) return DASH;
    const value = Number(detail?.[key] || 0);
    const showSign = negated && value > 0;
    return `${showSign ? '−' : ''}${money(value)}`;
  };

  const row = (label, key, { negated = false, hideWhenZero = false } = {}) => {
    const offered = Number(offeredDetail?.[key] || 0);
    const forecast = Number(forecastDetail?.[key] || 0);
    if (hideWhenZero && offered === 0 && forecast === 0) return null;
    return (
      <tr key={key}>
        <td className="ta-left">{label}</td>
        <td className="ta-right">{cell(offeredDetail, offeredImpossible, key, negated)}</td>
        {hasForecast && (
          <td className="ta-right">{cell(forecastDetail, forecastImpossible, key, negated)}</td>
        )}
      </tr>
    );
  };

  if (offeredImpossible && (!hasForecast || forecastImpossible)) {
    return (
      <div className="breakeven-tip">
        <p className="breakeven-tip__lead">
          There is no number of tickets at which this deal leaves the venue at zero or better —
          the artist&apos;s share grows at least as fast as the ticket revenue does. Lowering the
          percentage, adding a cap, or cutting an expense gives it a breakeven.
        </p>
      </div>
    );
  }

  return (
    <div className="breakeven-tip">
      <p className="breakeven-tip__lead">
        Tickets to breakeven is the smallest number of tickets at which the venue nets zero or
        better, solved against the whole deal — including the artist&apos;s percentage, not just the
        guarantee.
      </p>
      <table className="breakeven-tip__table">
        <thead>
          <tr>
            <th className="ta-left">At breakeven</th>
            <th className="ta-right">As Offered</th>
            {hasForecast && <th className="ta-right">Forecasted</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="ta-left">Tickets</td>
            <td className="ta-right">{breakevenCountLabel(offeredDetail)}</td>
            {hasForecast && <td className="ta-right">{breakevenCountLabel(forecastDetail)}</td>}
          </tr>
          {row('Ticket Revenue', 'ticketRevenueAtBreakeven')}
          {row('Other Income', 'otherIncomeAtBreakeven', { hideWhenZero: true })}
          {row('Before Adjusted Deductions', 'beforeAdjustedDeductionsAtBreakeven', { negated: true, hideWhenZero: true })}
          {row('Venue Expenses', 'venueExpensesAtBreakeven', { negated: true })}
          {row('Artist Payout', 'artistPayoutAtBreakeven', { negated: true })}
          <tr>
            <td className="breakeven-tip__rule" colSpan={columnCount}><hr /></td>
          </tr>
          <tr>
            <td className="ta-left"><strong>Venue Net</strong></td>
            <td className="ta-right">
              <strong>{offeredImpossible ? DASH : money(offeredDetail?.venueNetAtBreakeven)}</strong>
            </td>
            {hasForecast && (
              <td className="ta-right">
                <strong>{forecastImpossible ? DASH : money(forecastDetail?.venueNetAtBreakeven)}</strong>
              </td>
            )}
          </tr>
        </tbody>
      </table>
      {(offeredImpossible || forecastImpossible) && (
        <p className="breakeven-tip__note">
          A dash means no number of tickets leaves the venue at zero or better.
        </p>
      )}
      {offeredDetail?.beyondPlannedTickets && (
        <p className="breakeven-tip__note">
          More than the {count(offeredDetail?.totalTickets)} tickets this offer plans to sell.
        </p>
      )}
    </div>
  );
};

const breakevenDetailShape = PropTypes.shape({
  breakeven: PropTypes.number,
  achievable: PropTypes.bool,
  beyondPlannedTickets: PropTypes.bool,
  totalTickets: PropTypes.number,
  totalRevenue: PropTypes.number,
  weightedAverageTicketPrice: PropTypes.number,
  ticketRevenueAtBreakeven: PropTypes.number,
  otherIncomeAtBreakeven: PropTypes.number,
  beforeAdjustedDeductionsAtBreakeven: PropTypes.number,
  venueExpensesAtBreakeven: PropTypes.number,
  artistPayoutAtBreakeven: PropTypes.number,
  venueNetAtBreakeven: PropTypes.number,
});

TicketsToBreakevenTooltip.propTypes = {
  offeredDetail: breakevenDetailShape,
  forecastDetail: breakevenDetailShape,
};

TicketsToBreakevenTooltip.defaultProps = {
  offeredDetail: null,
  forecastDetail: null,
};

export default TicketsToBreakevenTooltip;
